import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type DentalShortcutRow = Database['public']['Tables']['dental_shortcuts']['Row'];

// ── Query keys ────────────────────────────────────────────────────────────────

export const shortcutsQueryKey = (clinicId: string | null | undefined) =>
  ['dental', 'shortcuts', clinicId ?? null] as const;

// ── Queries ───────────────────────────────────────────────────────────────────

export function useShortcuts(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: shortcutsQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<DentalShortcutRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('dental_shortcuts')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('label', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useUpsertShortcut(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id?: string;
      label: string;
      procedureCode: string;
      color: string;
      displayOrder?: number;
      isActive?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('upsert_dental_shortcut', {
        p_id: params.id ?? undefined,
        p_clinic_id: clinicId!,
        p_label: params.label,
        p_procedure_code: params.procedureCode,
        p_color: params.color,
        p_display_order: params.displayOrder ?? undefined,
        p_is_active: params.isActive ?? true,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: shortcutsQueryKey(clinicId) });
    },
  });
}

export function useDeleteShortcut(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_dental_shortcut', { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: shortcutsQueryKey(clinicId) });
    },
  });
}

export function useReorderShortcuts(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orders: Array<{ id: string; display_order: number }>) => {
      const { error } = await supabase.rpc('reorder_dental_shortcuts', {
        p_clinic_id: clinicId!,
        p_orders: orders as unknown as import('@/types/database.generated').Database['public']['Functions']['reorder_dental_shortcuts']['Args']['p_orders'],
      });
      if (error) throw error;
    },
    onSuccess: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: shortcutsQueryKey(clinicId) });
    },
  });
}

// ── All-shortcuts query (includes inactive, for admin page) ───────────────────

export function useAllShortcuts(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: [...shortcutsQueryKey(clinicId), 'all'],
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<DentalShortcutRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('dental_shortcuts')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('display_order', { ascending: true })
        .order('label', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}
